import React, { useState, useMemo, useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useNavigate, useSearchParams } from 'react-router-dom'
import api from '../services/api'
import { Device } from '../types'

interface DeviceSelectorProps {
  onDeviceSelect?: (device: Device) => void
  selectedDevice?: Device
  useNavigation?: boolean
}

const DeviceSelector: React.FC<DeviceSelectorProps> = ({
  onDeviceSelect,
  selectedDevice,
  useNavigation = true
}) => {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const [selectedBrand, setSelectedBrand] = useState<string>(searchParams.get('brand') || '')
  const [searchBrand, setSearchBrand] = useState<string>('')
  const [searchModel, setSearchModel] = useState<string>('')

  const handleDeviceClick = (device: Device) => {
    if (useNavigation) {
      // Navigate to new SEO URL
      navigate(`/${device.brandSlug}/${device.slug}`)
    } else if (onDeviceSelect) {
      // Use old callback method
      onDeviceSelect(device)
    }
  }

  // Scroll to top when a brand is selected (when switching from brands to models)
  useEffect(() => {
    if (selectedBrand) {
      window.scrollTo(0, 0)
    }
  }, [selectedBrand])

  const { data: devicesResponse, isLoading, error } = useQuery({
    queryKey: ['devices'],
    queryFn: api.getDevices
  })

  const { data: modelsResponse } = useQuery({
    queryKey: ['devices', selectedBrand],
    queryFn: () => api.getDevicesByBrand(selectedBrand),
    enabled: !!selectedBrand
  })

  const models = modelsResponse?.data || []
  const devicesByBrand = (devicesResponse?.data as Record<string, Device[]>) || {}

  // Create brands array from actual data
  const getBrandLogo = (brandName: string) => {
    switch (brandName) {
      case 'iPhone': return '/images/brands/apple-iphone.svg'
      case 'Samsung': return '/images/brands/samsung-logo.svg'
      case 'iPad': return '/images/brands/apple-ipad.svg'
      case 'MacBook': return '/images/brands/laptop-reparatur.svg'
      case 'Apple Watch': return '/images/brands/smartwatch-reparatur.svg'
      case 'Google': return '/images/brands/google-pixel-reparatur.svg'
      case 'Konsolen': return '/images/brands/konsolen-reparatur.svg'
      case 'Huawei': return '/images/brands/huawei-reparatur.svg'
      case 'Xiaomi': return '/images/brands/xiaomi-reparatur.svg'
      default: return null
    }
  }

  // Filter and search logic
  const allDevices = useMemo(() => {
    const devices: Device[] = []
    Object.values(devicesByBrand).forEach(brandDevices => {
      devices.push(...brandDevices)
    })
    return devices
  }, [devicesByBrand])

  // Get all device models as options, excluding problematic single characters
  const allModelNames = useMemo(() => {
    const modelNames = new Set<string>()
    const excludedModels = ['13', '14', '15', '16', '7', '8', 'X', '11', '12'] // Single numbers/letters to exclude
    
    allDevices.forEach(device => {
      // Use the full device model name
      const modelName = device.deviceModel
      
      // Skip if it's just a single number or letter that causes problems
      if (!excludedModels.includes(modelName.trim())) {
        modelNames.add(modelName)
      }
    })
    
    return Array.from(modelNames).sort()
  }, [allDevices])

  // Filtered devices based on search criteria
  const filteredDevices = useMemo(() => {
    let filtered = allDevices
    
    if (searchBrand) {
      filtered = filtered.filter(device => device.brand === searchBrand)
    }
    
    if (searchModel) {
      filtered = filtered.filter(device => {
        // Simple exact match with the full device model name
        return device.deviceModel === searchModel
      })
    }
    
    return filtered
  }, [allDevices, searchBrand, searchModel])

  // Group filtered devices by brand
  const filteredDevicesByBrand = useMemo(() => {
    const grouped: Record<string, Device[]> = {}
    filteredDevices.forEach(device => {
      if (!grouped[device.brand]) {
        grouped[device.brand] = []
      }
      grouped[device.brand].push(device)
    })
    return grouped
  }, [filteredDevices])

  const brands = Object.keys(devicesByBrand)
    .filter(brandName => brandName !== 'Motorola') // Remove Motorola
    .map(brandName => ({
      id: brandName,
      name: brandName,
      logo: getBrandLogo(brandName),
      description: `${brandName} Modelle`,
      count: devicesByBrand[brandName]?.length || 0
    }))

  const displayedBrands = Object.keys(filteredDevicesByBrand)
    .filter(brandName => brandName !== 'Motorola') // Remove Motorola
    .map(brandName => ({
      id: brandName,
      name: brandName,
      logo: getBrandLogo(brandName),
      description: `${brandName} Modelle`,
      count: filteredDevicesByBrand[brandName]?.length || 0
    }))

  if (isLoading) {
    return <div className="text-center py-8">Lade Geräte...</div>
  }

  if (error) {
    console.error('DeviceSelector error:', error)
    return <div className="text-center py-8 text-indigo-500">
      API-Fehler: {error instanceof Error ? error.message : String(error)}
      <br />
      <small>API URL: {import.meta.env.VITE_API_URL || 'http://localhost:5001/api'}</small>
      <br />
      <small>Origin: {window.location.origin}</small>
    </div>
  }

  if (!devicesResponse?.data || brands.length === 0) {
    return <div className="text-center py-8 text-orange-500">
      Keine Geräte verfügbar.
    </div>
  }

  const handleClearFilters = () => {
    setSearchBrand('')
    setSearchModel('')
  }

  return (
    <div className="space-y-8">
      {/* Search Filters */}
      <div className="bg-white p-6 rounded-xl shadow-lg">
        <h3 className="text-lg font-semibold mb-4">🔍 Geräte suchen</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          {/* Brand Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Marke auswählen
            </label>
            <select
              value={searchBrand}
              onChange={(e) => {
                setSearchBrand(e.target.value)
                setSearchModel('')
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            >
              <option value="">Alle Marken</option>
              {brands.map((brand) => (
                <option key={brand.id} value={brand.id}>
                  {brand.name} ({brand.count} Modelle)
                </option>
              ))}
            </select>
          </div>

          {/* Model Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Modellserie
            </label>
            <select
              value={searchModel}
              onChange={(e) => setSearchModel(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              disabled={!searchBrand}
            >
              <option value="">Alle Modelle</option>
              {searchBrand && allModelNames
                .filter(modelName => {
                  return allDevices.some(device => 
                    device.brand === searchBrand && 
                    (device.deviceModel.includes(modelName) || 
                     device.deviceModel.replace(device.brand, '').trim().includes(modelName))
                  )
                })
                .map((modelName) => (
                  <option key={modelName} value={modelName}>
                    {modelName}
                  </option>
                ))
              }
            </select>
          </div>
        </div>

        {/* Clear filters button and results count */}
        <div className="flex justify-between items-center">
          <button
            onClick={handleClearFilters}
            className="px-4 py-2 text-gray-600 hover:text-indigo-600 text-sm transition-colors"
            disabled={!searchBrand && !searchModel}
          >
            🗑️ Filter zurücksetzen
          </button>
          <span className="text-sm text-gray-600">
            {filteredDevices.length > 0 ? `${filteredDevices.length} Geräte gefunden` : 'Keine Geräte gefunden'}
          </span>
        </div>

        {/* Quick select from filtered results */}
        {filteredDevices.length > 0 && filteredDevices.length <= 10 && (
          <div className="mt-4">
            <h4 className="text-sm font-medium text-gray-700 mb-2">Gefundene Geräte:</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {filteredDevices.map((device) => (
                <button
                  key={device._id}
                  onClick={() => handleDeviceClick(device)}
                  className="p-3 border border-gray-200 rounded-lg hover:border-indigo-300 hover:bg-indigo-50 transition-all text-left"
                >
                  <div className="flex items-center space-x-3">
                    <div className="w-12 h-12 flex items-center justify-center overflow-hidden flex-shrink-0">
                      {device.image ? (
                        <img
                          src={device.image}
                          alt={device.deviceModel}
                          className="w-full h-full object-contain"
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            target.style.display = 'none';
                            target.parentElement!.innerHTML = `<div class="text-2xl">${
                              device.category === 'smartphone' ? '📱' :
                              device.category === 'tablet' ? '📋' :
                              device.category === 'laptop' ? '💻' :
                              device.category === 'smartwatch' ? '⌚' :
                              device.category === 'gaming' ? '🎮' : '📱'
                            }</div>`;
                          }}
                        />
                      ) : (
                        <div className="text-2xl">
                          {device.category === 'smartphone' ? '📱' :
                           device.category === 'tablet' ? '📋' :
                           device.category === 'laptop' ? '💻' :
                           device.category === 'smartwatch' ? '⌚' :
                           device.category === 'gaming' ? '🎮' : '📱'}
                        </div>
                      )}
                    </div>
                    <div>
                      <div className="font-medium text-sm">{device.deviceModel}</div>
                      <div className="text-xs text-gray-500">{device.brand}</div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Brand Selection */}
      {!selectedBrand && (
        <div>
          <h2 className="text-2xl font-bold text-center mb-8">Oder Marke auswählen</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {displayedBrands.length > 0 ? displayedBrands.map((brand) => (
              <button
                key={brand.id}
                onClick={() => { setSelectedBrand(brand.id); setSearchParams({ brand: brand.id }) }}
                className="bg-white p-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1 border-2 border-transparent hover:border-indigo-500"
              >
                <div className="h-16 mb-4 flex items-center justify-center">
                  {brand.logo ? (
                    <img
                      src={brand.logo}
                      alt={brand.name}
                      className="h-full w-auto object-contain"
                    />
                  ) : (
                    <div className="text-4xl">📱</div>
                  )}
                </div>
                <h3 className="text-xl font-semibold mb-2">{brand.name}</h3>
                <p className="text-gray-600 text-sm">{brand.description}</p>
                <p className="text-indigo-500 font-medium text-sm mt-2">{brand.count} Modelle verfügbar</p>
              </button>
            )) : (
              <div className="col-span-full text-center py-8 text-gray-500">
                Keine Marken entsprechen den aktuellen Filterkriterien.
              </div>
            )}
          </div>
        </div>
      )}

      {/* Model Selection */}
      {selectedBrand && models && (
        <div>
          <div className="flex items-center mb-6">
            <button
              onClick={() => { setSelectedBrand(''); setSearchParams({}) }}
              className="text-indigo-600 hover:text-indigo-700 mr-4"
            >
              ← Zurück
            </button>
            <h2 className="text-2xl font-bold">Modell auswählen</h2>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {models.map((device: Device) => (
              <button
                key={device._id}
                onClick={() => handleDeviceClick(device)}
                className={`p-4 rounded-lg border-2 transition-all hover:shadow-xl ${
                  selectedDevice?._id === device._id
                    ? 'border-indigo-500 bg-indigo-50'
                    : 'border-gray-200 hover:border-indigo-300 bg-white'
                }`}
              >
                <div className="w-full aspect-square flex items-center justify-center overflow-hidden mb-3">
                  {device.image ? (
                    <img
                      src={device.image}
                      alt={device.deviceModel}
                      className="w-full h-full object-contain"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.style.display = 'none';
                        target.parentElement!.innerHTML = `<div class="text-4xl">${
                          device.category === 'smartphone' ? '📱' :
                          device.category === 'tablet' ? '📋' :
                          device.category === 'laptop' ? '💻' :
                          device.category === 'smartwatch' ? '⌚' :
                          device.category === 'gaming' ? '🎮' : '📱'
                        }</div>`;
                      }}
                    />
                  ) : (
                    <div className="text-4xl">
                      {device.category === 'smartphone' ? '📱' :
                       device.category === 'tablet' ? '📋' :
                       device.category === 'laptop' ? '💻' :
                       device.category === 'smartwatch' ? '⌚' :
                       device.category === 'gaming' ? '🎮' : '📱'}
                    </div>
                  )}
                </div>
                <div className="text-center">
                  <h4 className="font-semibold text-sm mb-1">{device.deviceModel}</h4>
                  <p className="text-xs text-gray-600">{device.brand}</p>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

export default DeviceSelector